import { ReferenceField, TextField } from '@/components/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MessageSquare, Send } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext, useRefresh } from 'ra-core';
import { useState } from 'react';

export const DesignRevisionComments = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    
    // Using a fake user ID (e.g., 2) for the sake of the demo
    // In a real app, you would get this from useAuthState or a custom hook
    const currentUserId = 2; 

    const [comment, setComment] = useState('');
    const [loading, setLoading] = useState(false);

    if (!record) return null;

    const comments = Array.isArray(record.revision_comments) ? record.revision_comments : [];

    const handleAddComment = async () => {
        if (!comment.trim()) return;
        
        setLoading(true);
        try {
            const newComment = {
                id: Date.now().toString(),
                user_id: currentUserId,
                text: comment,
                created_at: new Date().toISOString()
            };

            await dataProvider.update('design_requests', {
                id: record.id,
                data: {
                    revision_comments: [...comments, newComment]
                },
                previousData: record
            });

            setComment('');
            notify('Comment added', { type: 'success' });
            refresh();
        } catch (error) {
            notify('Error adding comment', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card>
            <CardHeader className="pb-3 border-b bg-slate-50/50">
                <CardTitle className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" /> Discussion & Feedback
                </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
                <div className="space-y-6">
                    {comments.length === 0 ? (
                        <div className="text-center py-6 text-sm text-slate-400">
                            No feedback comments yet.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {comments.map((c: any) => (
                                <div key={c.id} className="flex gap-4">
                                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500 text-xs shadow-sm">
                                        <ReferenceField record={c} source="user_id" reference="sales" link={false}>
                                            <TextField source="first_name" className="uppercase" />
                                        </ReferenceField>
                                    </div>
                                    <div className="flex-1 bg-slate-50 rounded-lg p-3 text-sm text-slate-700">
                                        <div className="flex justify-between items-center mb-1">
                                            <div className="font-bold text-slate-900">
                                                <ReferenceField record={c} source="user_id" reference="sales" link={false}>
                                                    <TextField source="first_name" />{' '}
                                                    <TextField source="last_name" />
                                                </ReferenceField>
                                            </div>
                                            <span className="text-xs text-slate-400">
                                                {new Date(c.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                        <p className="leading-relaxed">{c.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex gap-2 pt-4 border-t border-slate-100">
                        <Input 
                            placeholder="Add your feedback or review comments..." 
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                        />
                        <Button 
                            onClick={handleAddComment} 
                            disabled={loading || !comment.trim()}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
